import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { fetcher, postFetcher, putFetcher } from 'api/swr'
import useSWRMutation from 'swr/mutation'
import AvatarListGrid from './components/AvatarListGrid'
import TemplateListGrid from './components/TemplateListGrid'
import type { AppListConfig, AppListSavedConfig } from './listConfigs'
import {
  buildAvatarListAfterCursor,
  parseAvatarListLimit,
  parseAvatarListResponse,
  type AvatarListItem,
} from './utils/avatarList'
import {
  buildTemplateListAfterCursor,
  parseTemplateListLimit,
  parseTemplateListResponse,
  type TemplateListItem,
} from './utils/templateList'

type FetchResult = {
  ok: boolean
  status: number
  url: string
  data: unknown
}

const toPrettyJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const parseJsonObject = (
  raw: string,
  fieldName: string,
): Record<string, string> => {
  const trimmed = raw.trim()
  if (!trimmed) return {}
  const parsed = JSON.parse(trimmed) as unknown
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${fieldName} must be a JSON object`)
  }
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (value == null) continue
    result[key] = String(value)
  }
  return result
}

const toFormState = (saved?: AppListSavedConfig, fallback?: AppListConfig) => ({
  path: saved?.path ?? fallback?.path ?? '',
  queryText: JSON.stringify(
    saved?.query ?? fallback?.defaultQuery ?? {},
    null,
    2,
  ),
  headersText: JSON.stringify(
    saved?.headers ?? fallback?.defaultHeaders ?? {},
    null,
    2,
  ),
})

const stripAfterFromQuery = (query: Record<string, string>) => {
  const next = { ...query }
  delete next.after
  return next
}

type Props = {
  config: AppListConfig
}

export default function AppListTabPanel({ config }: Props) {
  const { toast } = useToast()
  const {
    data: savedConfig,
    isLoading,
    mutate,
  } = useSWR<AppListSavedConfig>([`/app-lists/configs/${config.id}`], fetcher)
  const [path, setPath] = useState(config.path)
  const [queryText, setQueryText] = useState(() =>
    JSON.stringify(config.defaultQuery, null, 2),
  )
  const [headersText, setHeadersText] = useState(() =>
    JSON.stringify(config.defaultHeaders, null, 2),
  )
  const [result, setResult] = useState<FetchResult | null>(null)
  const [templateItems, setTemplateItems] = useState<TemplateListItem[]>([])
  const [avatarItems, setAvatarItems] = useState<AvatarListItem[]>([])
  const [nextAfter, setNextAfter] = useState<string | null>(null)
  const [currentPageAfter, setCurrentPageAfter] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingNext, setIsLoadingNext] = useState(false)

  const isListPanel = config.resultType === 'template' || config.resultType === 'avatar'
  const avatarListMode = config.id === 'avatar-new' ? 'new' : 'daily'

  const { trigger: fetchTrigger, isMutating: isFetching } = useSWRMutation(
    '/app-lists/fetch',
    postFetcher,
  )
  const { trigger: saveTrigger, isMutating: isSaving } = useSWRMutation(
    `/app-lists/configs/${config.id}`,
    putFetcher,
  )

  const resetListPagination = useCallback(() => {
    setTemplateItems([])
    setAvatarItems([])
    setNextAfter(null)
    setCurrentPageAfter(null)
    setHasMore(false)
    setIsLoadingNext(false)
  }, [])

  useEffect(() => {
    if (isLoading) return
    const next = toFormState(savedConfig, config)
    setPath(next.path)
    setQueryText(next.queryText)
    setHeadersText(next.headersText)
    setResult(null)
    resetListPagination()
  }, [config, isLoading, resetListPagination, savedConfig])

  const requestPreview = useMemo(() => {
    try {
      const query = stripAfterFromQuery(parseJsonObject(queryText, 'query'))
      const params = new URLSearchParams(query)
      const suffix = params.toString()
      return `${path}${suffix ? `?${suffix}` : ''}`
    } catch {
      return path
    }
  }, [path, queryText])

  const readRequestConfig = useCallback(
    (paginationAfter?: string) => {
      const trimmedPath = path.trim()
      if (!trimmedPath.startsWith('/')) {
        throw new Error('path must start with /')
      }
      const query = stripAfterFromQuery(parseJsonObject(queryText, 'query'))
      if (paginationAfter) {
        query.after = paginationAfter
      }
      return {
        path: trimmedPath,
        query,
        headers: parseJsonObject(headersText, 'headers'),
      }
    },
    [headersText, path, queryText],
  )

  const applyListPage = useCallback(
    (response: FetchResult, append: boolean) => {
      const requestQuery = stripAfterFromQuery(
        parseJsonObject(queryText, 'query'),
      )

      if (config.resultType === 'template') {
        const pageItems = parseTemplateListResponse(response.data)
        const limit = parseTemplateListLimit(requestQuery)
        const pageEnd = pageItems[pageItems.length - 1]

        setTemplateItems((prev) =>
          append ? [...prev, ...pageItems] : pageItems,
        )
        setNextAfter(
          pageEnd
            ? buildTemplateListAfterCursor(pageEnd, requestQuery.type)
            : null,
        )
        setHasMore(pageItems.length >= limit)
      } else if (config.resultType === 'avatar') {
        const pageItems = parseAvatarListResponse(response.data)
        const limit = parseAvatarListLimit(requestQuery)
        const pageEnd = pageItems[pageItems.length - 1]

        setAvatarItems((prev) => (append ? [...prev, ...pageItems] : pageItems))
        setNextAfter(
          pageEnd
            ? buildAvatarListAfterCursor(pageEnd, requestQuery.type, avatarListMode)
            : null,
        )
        setHasMore(pageItems.length >= limit)
      }

      setResult(response)
    },
    [avatarListMode, config.resultType, queryText],
  )

  const runFetch = useCallback(
    async (paginationAfter?: string, append = false) => {
      let requestConfig: {
        path: string
        query: Record<string, string>
        headers: Record<string, string>
      }
      try {
        requestConfig = readRequestConfig(paginationAfter)
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err)
        toast({
          variant: 'destructive',
          title: '参数格式错误',
          description: message,
        })
        return false
      }

      try {
        const response = (await fetchTrigger(requestConfig)) as FetchResult
        if (isListPanel) {
          applyListPage(response, append)
        } else {
          setResult(response)
        }
        return true
      } catch (err: unknown) {
        if (!append) {
          setResult(null)
          resetListPagination()
        }
        const message = err instanceof Error ? err.message : String(err)
        toast({
          variant: 'destructive',
          title: '请求失败',
          description: message,
        })
        return false
      }
    },
    [
      applyListPage,
      fetchTrigger,
      isListPanel,
      readRequestConfig,
      resetListPagination,
      toast,
    ],
  )

  const onFetch = useCallback(async () => {
    resetListPagination()
    await runFetch(undefined, false)
  }, [resetListPagination, runFetch])

  const onLoadNextPage = useCallback(async () => {
    if (!nextAfter || !hasMore || isLoadingNext) return
    setIsLoadingNext(true)
    setCurrentPageAfter(nextAfter)
    try {
      await runFetch(nextAfter, true)
    } finally {
      setIsLoadingNext(false)
    }
  }, [hasMore, isLoadingNext, nextAfter, runFetch])

  const onSave = useCallback(async () => {
    let requestConfig: {
      path: string
      query: Record<string, string>
      headers: Record<string, string>
    }
    try {
      requestConfig = readRequestConfig()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: '参数格式错误',
        description: message,
      })
      return
    }

    try {
      const saved = (await saveTrigger(requestConfig)) as AppListSavedConfig
      await mutate(saved, false)
      toast({
        title: '配置已保存',
        description: `${config.label} 已写入 Firestore`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: '保存失败',
        description: message,
      })
    }
  }, [config.label, mutate, readRequestConfig, saveTrigger, toast])

  const onReset = () => {
    const next = toFormState(undefined, config)
    setPath(next.path)
    setQueryText(next.queryText)
    setHeadersText(next.headersText)
    setResult(null)
    resetListPagination()
  }

  const loadedCount =
    config.resultType === 'template'
      ? templateItems.length
      : config.resultType === 'avatar'
        ? avatarItems.length
        : 0

  const listPanelTitle =
    config.resultType === 'template' ? '模版列表' : 'Avatar 列表'

  return (
    <div className="h-full">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">加载配置中…</p>
      ) : (
        <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
            <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {isListPanel ? listPanelTitle : '响应 JSON'}
                </p>
                {result ? (
                  <p className="text-xs text-muted-foreground break-all">
                    {result.status} · {result.url}
                    {isListPanel ? ` · 已加载 ${loadedCount} 条` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground break-all">
                    预览：{requestPreview}
                  </p>
                )}
              </div>
              <Button onClick={onFetch} disabled={isFetching || isLoadingNext}>
                {isFetching ? '请求中…' : '发送请求'}
              </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {config.resultType === 'template' ? (
                templateItems.length > 0 ? (
                  <TemplateListGrid
                    items={templateItems}
                    hasMore={hasMore}
                    loadingNext={isLoadingNext}
                    onLoadNext={onLoadNextPage}
                  />
                ) : (
                  <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
                    点击「发送请求」查看模版列表
                  </div>
                )
              ) : config.resultType === 'avatar' ? (
                avatarItems.length > 0 ? (
                <AvatarListGrid
                  items={avatarItems}
                  hasMore={hasMore}
                  loadingNext={isLoadingNext}
                  onLoadNext={onLoadNextPage}
                />
                ) : (
                  <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
                    点击「发送请求」查看 Avatar 列表
                  </div>
                )
              ) : (
                <pre className="min-h-0 flex-1 overflow-y-auto p-4 text-xs leading-relaxed">
                  {result
                    ? toPrettyJson(result.data)
                    : '点击「发送请求」查看 Mask 接口返回数据'}
                </pre>
              )}
            </div>
          </div>

          <div className="flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-lg border lg:w-[420px]">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            {savedConfig?.updatedAt ? (
              <p className="text-xs text-muted-foreground">
                上次保存：
                {new Date(savedConfig.updatedAt).toLocaleString()}
                {savedConfig.updatedBy ? ` · ${savedConfig.updatedBy}` : ''}
              </p>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor={`${config.id}-path`}>Path</Label>
              <Input
                id={`${config.id}-path`}
                value={path}
                onChange={(e) => setPath(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${config.id}-query`}>Query（JSON 对象）</Label>
              <Textarea
                id={`${config.id}-query`}
                className="min-h-[180px] font-mono text-xs"
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${config.id}-headers`}>Headers（JSON 对象）</Label>
              <Textarea
                id={`${config.id}-headers`}
                className="min-h-[120px] font-mono text-xs"
                value={headersText}
                onChange={(e) => setHeadersText(e.target.value)}
              />
            </div>

            {isListPanel && currentPageAfter ? (
              <div className="space-y-2">
                <Label>分页参数</Label>
                <div className="rounded-md border bg-muted/40 p-3 font-mono text-xs break-all">
                  after: {currentPageAfter}
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button onClick={onSave} disabled={isSaving}>
                {isSaving ? '保存中…' : '保存配置'}
              </Button>
              <Button variant="outline" onClick={onReset}>
                恢复默认
              </Button>
            </div>

            {isListPanel ? (
              <div className="space-y-2">
                <Label>返回内容预览</Label>
                <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs leading-relaxed">
                  {result
                    ? toPrettyJson(result.data)
                    : '发送请求后在此展示接口返回 JSON'}
                </pre>
              </div>
            ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
